import { Component, EventEmitter, Output } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ModalServiceService } from '@core/services/modal-service.service';
import { UserProfileService } from '@core/services/user.service';

import { OnboardingService } from '../shared/services/onboarding.service';

interface WorkSpaceForm {
  workspace: FormControl;
}
@Component({
  selector: 'app-onboarding-step-two',
  templateUrl: './onboarding-step-two.component.html',
  styleUrl: './onboarding-step-two.component.scss',
  standalone: false,
})
export class OnboardingStepTwoComponent {
  value: string | undefined;
  submitted: boolean = false;
  @Output() nextStep = new EventEmitter<any>(false);
  isLoading = false;
  workSpaceForm!: FormGroup<WorkSpaceForm>;
  private destroy$ = new Subject<void>();

  constructor(
    private onboardingService: OnboardingService,
    private router: Router,
    private modelService: ModalServiceService,
    private userProfileService: UserProfileService
  ) {
    this.workSpaceForm = new FormGroup({
      workspace: new FormControl('', [Validators.required, this.noWhitespaceValidator]),
    });
  }

  noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true }; // Return an error object if invalid
  }

  submit() {
    this.submitted = true;
    if (!this.workSpaceForm.valid) return;
    this.isLoading = true;
    this.onboardingService.createWorkspace(this.workSpaceForm.controls.workspace.value).subscribe({
      next: () => {
        this.isLoading = false;
        this.nextStep.emit({ moveToNextStep: true, updateUser: true });
      },
      error: (err) => {
        this.modelService.openErrorModal(err.message);
        this.isLoading = false;
      },
    });
  }

  skipOnboarding() {
    this.isLoading = true;
    const randomName = 'Confe_' + Math.random().toString(36).substring(2, 8);
    this.onboardingService
      .createWorkspace(randomName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.onboardingService
            .setOnboardingForUser()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.userProfileService
                  .getUserDetails()
                  .pipe(takeUntil(this.destroy$))
                  .subscribe({
                    next: () => {
                      this.router.navigate(['/'], { replaceUrl: true });
                      this.isLoading = false;
                    },
                    error: () => {
                      // Even if refresh fails, proceed with navigation
                      this.router.navigate(['/'], { replaceUrl: true });
                      this.isLoading = false;
                    },
                  });
              },
              error: (err) => {
                this.modelService.openErrorModal(err.message);
                this.isLoading = false;
              },
            });
        },
        error: (err) => {
          this.modelService.openErrorModal(err.message);
          this.isLoading = false;
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
